import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReviews } from '../redux/reviewsSlice';
import moment from 'moment';
import { filterReviewsByPlatform } from '../utility/reviewByPlatforms';
import './dashboard.css';

const Dashboard = () => {

  const formatDate = (dateString) => {
    if (!dateString) return 'No Date';
    const formattedDate = moment(dateString, 'DD/MM/YYYY');
    return formattedDate.isValid() ? formattedDate.format('DD/MM/YYYY') : 'Invalid Date';
  };

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedRating, setSelectedRating] = useState('');
  const [selectedProductCode, setSelectedProductCode] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');

  const dispatch = useDispatch();
  const { reviews, loading, error } = useSelector((state) => state.reviews);

  // ✅ FIX: prevent repeated API calls
  useEffect(() => {
    if (!reviews || reviews.length === 0) {
      dispatch(fetchReviews());
    }
  }, [dispatch, reviews]);

  const availablePlatforms = [
    { code: 'GYG', name: 'Get Your Guide' },
    { code: 'HEA', name: 'Headout' },
    { code: 'TIQ', name: 'Tiqets' },
    { code: 'VIATOR', name: 'Viator' },
  ];

  // ✅ SAFE destructuring
  const {
    gygReviews = [],
    headoutReviews = [],
    tiqetsReviews = [],
    viatorReviews = [],
  } = filterReviewsByPlatform(reviews) || {};

  // ✅ OPTIMIZED filtering
  const filteredReviews = useMemo(() => {
  return (reviews || []).filter((review) => {
    // ✅ HARD GUARD
    if (!Array.isArray(review)) return false;

    const reviewDate = typeof review[3] === 'string' ? review[3] : null;
    const reviewRating = review[4] ?? null;
    const reviewProductCode = review[6] ?? '';

    // ✅ ALWAYS STRING
    const productCodeStr = String(reviewProductCode).toUpperCase();

    const isWithinDateRange =
      fromDate && toDate
        ? moment(reviewDate, 'DD/MM/YYYY').isBetween(
            moment(fromDate, 'YYYY-MM-DD'),
            moment(toDate, 'YYYY-MM-DD'),
            undefined,
            '[]'
          )
        : true;

    const matchesRating = selectedRating
      ? reviewRating === parseInt(selectedRating)
      : true;

    const matchesProductCode = selectedProductCode
      ? reviewProductCode === selectedProductCode
      : true;

    const platformMatch =
  !selectedPlatform ||
  (selectedPlatform === 'GYG' && gygReviews.includes(review)) ||
  (selectedPlatform === 'HEA' && headoutReviews.includes(review)) ||
  (selectedPlatform === 'TIQ' && tiqetsReviews.includes(review)) ||
  (selectedPlatform === 'VIATOR' && viatorReviews.includes(review));



      return isWithinDateRange && matchesRating && matchesProductCode && platformMatch;
    });
  }, [
    reviews,
    fromDate,
    toDate,
    selectedRating,
    selectedProductCode,
    selectedPlatform,
    gygReviews,
    headoutReviews,
    tiqetsReviews,
    viatorReviews,
  ]);

  const isFilterApplied =
    fromDate || toDate || selectedRating || selectedProductCode || selectedPlatform;

  // ✅ memoized product codes (big performance gain)
  const productCodes = useMemo(() => {
    return Array.from(
      new Set(reviews.map((review) => review[6]).filter((code) => code))
    );
  }, [reviews]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error loading data: {error.message}</p>;

  return (
    <div className='filterContainer'>
      <div className='filter'>
        <div>
          <label className='labels'>
            From Date:
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>
          <label className='labels'>
            To Date:
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>
        </div>

        <div>
          <label className='labels'>
            Select Star Rating:
            <select
              value={selectedRating}
              onChange={(e) => setSelectedRating(e.target.value)}
            >
              <option value="">All</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
          </label>
        </div>

        <div>
          <label>
            Select Product Code:
            <select
              value={selectedProductCode}
              onChange={(e) => setSelectedProductCode(e.target.value)}
            >
              <option value="">All</option>
              {productCodes.map((code, index) => (
                <option key={index} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <label>
            Select Platform:
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
            >
              <option value="">All</option>
              {availablePlatforms.map((platform, index) => (
                <option key={index} value={platform.code}>
                  {platform.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div>
        <table className='dataTable'>
          <thead>
            <tr>
              <th>Booking Id</th>
              <th>Rating</th>
              <th>Date</th>
              <th>Product Code</th>
              <th>Guide</th>
            </tr>
          </thead>
          <tbody>
            {(isFilterApplied ? filteredReviews : reviews).map((review, index) => (
              <tr key={index}>
                <td>{review && review.length > 1 ? review[1] : 'No Id'}</td>
                <td>{review && review.length > 4 ? review[4] : 'No Rating'}</td>
                <td>
                  {review && review.length > 3 && typeof review[3] === 'string'
                    ? review[3].split('T')[0]
                    : 'No Date'}
                </td>
                <td>{review && review.length > 6 ? review[6] : 'Product Code'}</td>
                <td>{review && review.length > 10 ? review[10] : 'No Guide'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;